import { Mode } from '@hewey/shared'
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	type ReactNode,
} from 'react'
import { useLocalStorage } from '@hooks/useLocalStorage.ts'

// The "mode" is a global lens across all mini-apps: when the user flips it
// in one app, every other app sees the same value. Persisted in localStorage
// (per-device UI preference, not user data).

const STORAGE_KEY = 'hewey.mode'
const DEFAULT_MODE: Mode = 'personal'

type ModeCtx = {
	mode: Mode
	setMode: (next: Mode) => void
}

const Ctx = createContext<ModeCtx | null>(null)

export const ModeProvider = ({ children }: { children: ReactNode }) => {
	const [stored, setStored] = useLocalStorage<Mode>(STORAGE_KEY, DEFAULT_MODE)

	// Validate at the boundary — localStorage can be hand-edited or stale
	// from a previous app version that used different values.
	const mode: Mode = Mode.safeParse(stored).success
		? (stored as Mode)
		: DEFAULT_MODE

	const setMode = useCallback(
		(next: Mode) => {
			setStored(next)
		},
		[setStored],
	)

	const value = useMemo(() => ({ mode, setMode }), [mode, setMode])
	return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export const useMode = (): ModeCtx => {
	const ctx = useContext(Ctx)
	if (!ctx) throw new Error('useMode must be used inside <ModeProvider>')
	return ctx
}
